import { Component, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { AuthService, ServerRegion } from '../services/auth.service';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-login',
  imports: [
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatIconModule,
    MatProgressBarModule,
    MatToolbarModule
  ],
  templateUrl: './login.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './login.component.scss'
})
export class LoginComponent {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly snackBar = inject(MatSnackBar);

  /** 'credentials' = email/password step, 'mfa' = code step. */
  readonly step = signal<'credentials' | 'mfa'>('credentials');
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly showDemo = signal(false);
  passwordFieldType = signal('password');

  /** Example accounts (one per role, per region) shown for quick sign-in. */
  // readonly demoAccounts = this.auth.demoCredentials;

  readonly form = new FormGroup({
    region: new FormControl<ServerRegion>('US', { nonNullable: true }),
    email: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.email], updateOn: 'blur'}),
    password: new FormControl('', { nonNullable: true, validators: [Validators.required], updateOn: 'blur'}),
    mfaCode: new FormControl('', { nonNullable: true, validators: [Validators.pattern(/^\d{6}$/)], updateOn: 'blur'})
  });

  togglePasswordVisibility() {
    switch (this.passwordFieldType()) {
      case 'password':
        this.passwordFieldType.set('text');
        break;
      case 'text':
        this.passwordFieldType.set('password');
        break;
      default:
        this.passwordFieldType.set('password');
        break;
    }
  }

  submitCredentials(): void {
    if (this.form.controls.email.invalid || this.form.controls.password.invalid) {
      this.form.controls.email.markAsTouched();
      this.form.controls.password.markAsTouched();
      return;
    }
    this.error.set(null);
    this.loading.set(true);

    const { email, password } = this.form.getRawValue();

    this.auth.login(email, password).subscribe({
      next: response => {
        this.loading.set(false);
        console.log(response.message);
        this.snackBar.open(response.message, 'Dismiss', { duration: 5000 });
        this.step.set('mfa');
      },
      error: err => {
        this.loading.set(false);
        this.error.set(err.message ?? 'Sign-in failed.');
      }
    });

  }

  forgotPassword() {
    this.router.navigate(['/forgot-passphrase']);
  }

  /* submitCredentials(): void {
    if (this.form.controls.email.invalid || this.form.controls.password.invalid) {
      this.form.controls.email.markAsTouched();
      this.form.controls.password.markAsTouched();
      return;
    }
    this.error.set(null);
    this.loading.set(true);

    const { email, password, region } = this.form.getRawValue();
    this.auth.login(email, password, region).subscribe({
      next: () => {
        this.loading.set(false);
        this.router.navigate(['/']);
      },
      error: err => {
        this.loading.set(false);
        this.error.set(err.error?.message ?? err.message ?? 'Sign-in failed.');
      }
    });
  } */

  resendOTP(): void {
    if (this.form.controls.email.invalid || this.form.controls.password.invalid) {
      this.form.controls.email.markAsTouched();
      this.form.controls.password.markAsTouched();
      return;
    }
    this.error.set(null);
    this.loading.set(true);

    const { email, password } = this.form.getRawValue();

    this.auth.resendOTP(email).subscribe({
      next: response => {
        this.loading.set(false);
        console.log(response.message);
        this.snackBar.open(response.message, 'Dismiss', { duration: 5000 });
        this.step.set('mfa');
      },
      error: err => {
        this.loading.set(false);
        this.error.set(err.message ?? 'Resend OTP Failed.');
      }
    });

  }
  

  submitMFA(): void {
    if (this.form.controls.mfaCode.invalid) {
      this.form.controls.mfaCode.markAsTouched();
      return;
    }
    this.error.set(null);
    this.loading.set(true);

    const { email, mfaCode, region } = this.form.getRawValue();

    this.auth.verifyMFA(email, mfaCode, region).subscribe({
      next: () => {
        this.loading.set(false);
        this.router.navigate(['/']);
      },
      error: err => {
        this.loading.set(false);
        this.error.set(err.message ?? 'Verification failed.');
      }
    });
  }

  backToCredentials(): void {
    this.error.set(null);
    this.form.controls.mfaCode.reset('');
    this.step.set('credentials');
  }

  copyEmail() {
    navigator.clipboard.writeText('support@privacy4cars.com').finally(() => {
      this.snackBar.open('Email ID Copied to Clipboard', 'Dismiss', { duration: 5000 });
    })
  }

  /** Prefill the form from a demo account for quick testing. */
  // useDemoAccount(account: DemoCredential): void {
  //   this.error.set(null);
  //   this.form.patchValue({
  //     region: account.region,
  //     email: account.email,
  //     password: account.password
  //   });
  // }
}
